import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { FaShoppingCart } from 'react-icons/fa';
import { content } from '@/content/sr';
import { CONTACT_FORM_PATH } from '@/constants/paths';
import { useCart } from '@/context/CartContext';

/** Link ka korpi sa brojem stavki; badge se krije kad je korpa prazna. */
const CartLink = ({ itemCount, onClick, mobile = false }) => (
  <Link
    to="/korpa"
    onClick={onClick}
    className={`relative p-2 text-brand-primary hover:text-brand-accent transition-colors ${mobile ? '' : 'mr-1'}`}
    aria-label={itemCount > 0 ? `Korpa, ${itemCount} stavki` : 'Korpa'}
    data-testid={mobile ? 'mobile-cart-link' : 'header-cart-link'}
  >
    <FaShoppingCart size={mobile ? 22 : 18} />
    {itemCount > 0 && (
      <span
        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-accent
                   text-white text-[11px] font-medium flex items-center justify-center"
        data-testid="cart-badge"
      >
        {itemCount > 99 ? '99+' : itemCount}
      </span>
    )}
  </Link>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: content.navigation.home },
    { path: '/o-nama', label: content.navigation.about },
    { path: '/proizvodi', label: content.navigation.products },
    { path: '/shop', label: content.navigation.shop },
    { path: '/projekti', label: content.navigation.projects },
  ];

  const isActive = (path) => location.pathname === path;
  const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-md py-3' : 'bg-transparent py-5'
      }`}
      data-testid="header"
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
            onClick={scrollToTop}
          >
            <div className="flex flex-col">
              <span className="font-heading text-2xl md:text-3xl font-semibold text-brand-primary tracking-tight">
                Wall <span className="text-brand-accent">Lux</span>
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 hidden sm:block">
                {content.company.tagline}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={scrollToTop}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-brand-accent'
                    : 'text-brand-primary hover:text-brand-accent'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <CartLink itemCount={itemCount} onClick={scrollToTop} />
            <Link to={CONTACT_FORM_PATH} className="btn-accent text-xs py-3 px-6" data-testid="header-cta">
              SPREMNI ZA SARADNJU?
            </Link>
          </div>

          {/* Mobile: korpa + meni */}
          <div className="lg:hidden flex items-center gap-1">
            <CartLink itemCount={itemCount} onClick={scrollToTop} mobile />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-brand-primary hover:text-brand-accent transition-colors"
            aria-label={isMobileMenuOpen ? 'Zatvori meni' : 'Otvori meni'}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <HiX size={28} /> : <HiMenu size={28} />}
          </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-neutral-200"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={scrollToTop}
                    className={`text-base font-medium py-2 transition-colors ${
                      isActive(link.path)
                        ? 'text-brand-accent'
                        : 'text-brand-primary hover:text-brand-accent'
                    }`}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 border-t border-neutral-200">
                  <Link to={CONTACT_FORM_PATH} className="btn-accent w-full text-center block" data-testid="mobile-cta">
                    Saradjujmo zajedno
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
